import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Settings,
  Clock,
  Save,
  CheckCircle,
  AlertCircle,
  Bell,
  User
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getUserPreferences, updateUserPreferences, synchronizeSearchIntervals } from '../lib/firebaseService';
import { toast } from '../hooks/use-toast';
import NotificationSettings from '../components/NotificationSettings';

const SettingsPage = ({ onBack }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('refresh');
  const [preferences, setPreferences] = useState({
    defaultFetchInterval: 60,
    enableNotifications: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      loadUserPreferences();
    }
  }, [user]);

  const loadUserPreferences = async () => {
    try {
      setLoading(true);
      const result = await getUserPreferences(user.uid);
      if (result.success) {
        setPreferences(result.preferences);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Save user preferences
      const result = await updateUserPreferences(user.uid, preferences);
      if (!result.success) {
        throw new Error('Erreur lors de la sauvegarde des préférences');
      }

      // Synchronize existing searches with the new default interval
      const syncResult = await synchronizeSearchIntervals(user.uid, preferences.defaultFetchInterval);
      if (syncResult.success) {
        const message = syncResult.updatedCount > 0
          ? `Préférences sauvegardées et ${syncResult.updatedCount} recherche(s) mise(s) à jour.`
          : "Préférences sauvegardées avec succès.";

        toast({
          title: "Paramètres sauvegardés",
          description: message,
        });
      } else {
        // Preferences saved but sync failed
        toast({
          title: "Paramètres partiellement sauvegardés",
          description: "Préférences sauvegardées, mais erreur lors de la synchronisation des recherches.",
          variant: "warning",
        });
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder les paramètres.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const intervalOptions = [
    { value: 2, label: '2 minutes' },
    { value: 5, label: '5 minutes' },
    { value: 10, label: '10 minutes' },
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 60, label: '1 heure' },
    { value: 120, label: '2 heures' },
    { value: 240, label: '4 heures' },
    { value: 480, label: '8 heures' }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <Card className="animate-pulse">
          <CardHeader>
            <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const tabs = [
    {
      id: 'refresh',
      name: 'Actualisation',
      icon: Clock,
      description: 'Paramètres d\'actualisation automatique'
    },
    {
      id: 'notifications',
      name: 'Notifications',
      icon: Bell,
      description: 'Gérer vos notifications'
    },
    {
      id: 'profile',
      name: 'Profil',
      icon: User,
      description: 'Informations du compte'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'refresh':
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-blue-600" />
                <span>Actualisation automatique</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Interval Setting */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Intervalle par défaut pour l'actualisation automatique
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {intervalOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setPreferences({
                        ...preferences,
                        defaultFetchInterval: option.value
                      })}
                      className={`
                        p-3 rounded-lg border-2 text-sm font-medium transition-all
                        ${preferences.defaultFetchInterval === option.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Cet intervalle sera utilisé par défaut pour toutes les nouvelles recherches avec actualisation automatique activée.
                </p>
              </div>

              {/* Current Setting Display */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium text-gray-700">
                      Intervalle actuel
                    </span>
                  </div>
                  <Badge variant="secondary">
                    {intervalOptions.find(opt => opt.value === preferences.defaultFetchInterval)?.label || `${preferences.defaultFetchInterval} minutes`}
                  </Badge>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex space-x-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">À propos de l'actualisation automatique :</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>Vous pouvez activer/désactiver l'actualisation automatique individuellement pour chaque recherche</li>
                      <li>Les recherches avec actualisation automatique vérifieront les nouvelles annonces à l'intervalle configuré</li>
                      <li>Vous recevrez une notification lorsque de nouvelles annonces sont trouvées</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-4 border-t border-gray-200">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center space-x-2"
                >
                  <Save className="w-4 h-4" />
                  <span>{saving ? 'Sauvegarde...' : 'Sauvegarder les paramètres'}</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        );

      case 'notifications':
        return <NotificationSettings userId={user?.uid} />;

      case 'profile':
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="w-5 h-5 text-blue-600" />
                <span>Informations du profil</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  {user?.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt="Profile"
                      className="w-16 h-16 rounded-full"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                      <User className="h-8 w-8 text-gray-500" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{user?.displayName || 'Utilisateur'}</h3>
                    <p className="text-gray-600">{user?.email}</p>
                    {user?.emailVerified ? (
                      <Badge variant="success" className="mt-1">Email vérifié</Badge>
                    ) : (
                      <Badge variant="warning" className="mt-1">Email non vérifié</Badge>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom d'utilisateur
                    </label>
                    <input
                      type="text"
                      value={user?.displayName || ''}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <Settings className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Paramètres</h1>
            <p className="text-gray-600">Configurez vos préférences et notifications</p>
          </div>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            Retour
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="lg:w-1/4">
          <Card>
            <CardContent className="p-2">
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        activeTab === tab.id
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <div>
                        <div className="font-medium">{tab.name}</div>
                        <div className="text-xs text-gray-500">{tab.description}</div>
                      </div>
                    </button>
                  );
                })}
              </nav>
            </CardContent>
          </Card>
        </div>

        {/* Main content */}
        <div className="lg:w-3/4">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;